import React, { useState, useMemo } from 'react';
import { NavArrowDown, NavArrowLeft } from 'iconoir-react';
import { C, St, FONT_BODY } from '../theme';
import { SearchBar } from '../components/UI';
import { findExercise } from '../exerciseDB';
import { EXERCISE_DB } from '../exerciseDB';
import { normalizeHevyName } from '../parsers';
import { ExerciseDetailView } from '../components/Exercise';

// ─── ESERCIZI PAGE ───────────────────────────────────────────────────────────
export function EserciziPage({ db, onBack }) {
  const [selectedEx, setSelectedEx] = useState(null);
  const [search, setSearch] = useState("");

  const sessionCount = useMemo(() => {
    const map = {};
    db.sessions.forEach(sess => sess.exercises.forEach(ex => {
      const canonical = findExercise(normalizeHevyName(ex.name))?.name || ex.name;
      map[canonical] = (map[canonical] || 0) + 1;
    }));
    return map;
  }, [db.sessions]);

  const [showLibrary, setShowLibrary] = useState(false);

  const filtered = useMemo(() =>
    EXERCISE_DB.filter(e =>
      !search ||
      e.name.toLowerCase().includes(search.toLowerCase()) ||
      (e.primaryMuscle || "").toLowerCase().includes(search.toLowerCase())
    )
    , [search]);

  const groupBy = (list) => {
    const map = {};
    list.forEach(e => { const m = e.primaryMuscle || "Altro"; if (!map[m]) map[m] = []; map[m].push(e); });
    return map;
  };

  const schedaExNames = useMemo(() => {
    const names = new Set();
    (db.schede || []).forEach(sc =>
      (sc.exercises || []).forEach(ex => {
        const canonical = findExercise(normalizeHevyName(ex.name))?.name || ex.name;
        names.add(canonical);
      })
    );
    return names;
  }, [db.schede]);

  const usedGrouped = useMemo(() => {
    const map = groupBy(filtered.filter(e => (sessionCount[e.name] || 0) > 0 || schedaExNames.has(e.name)));
    return Object.entries(map).sort((a, b) => {
      const tA = a[1].reduce((s, e) => s + (sessionCount[e.name] || 0), 0);
      const tB = b[1].reduce((s, e) => s + (sessionCount[e.name] || 0), 0);
      return tB - tA;
    });
  }, [filtered, sessionCount, schedaExNames]);

  const unusedGrouped = useMemo(() => {
    const map = groupBy(filtered.filter(e => !(sessionCount[e.name])));
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered, sessionCount]);

  // Se è selezionato un esercizio, mostra la detail view
  if (selectedEx) return <ExerciseDetailView name={selectedEx} sessions={db.sessions} onBack={() => setSelectedEx(null)} />;

  return (
    <div style={St.wrap}>
      <div style={{ paddingTop: "max(56px, calc(env(safe-area-inset-top, 0px) + 14px))", paddingRight: "20px", paddingBottom: "8px", paddingLeft: "20px", background: C.bg }}>
        {onBack && (
          <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', color: C.blue, padding: '0 0 10px', fontFamily: FONT_BODY, fontSize: 17, fontWeight: 400 }}>
            <NavArrowLeft width={16} height={16} strokeWidth={2} />
            Indietro
          </button>
        )}
        <h1 style={{ margin: 0, fontFamily: FONT_BODY, fontWeight: 800, fontSize: 34, color: C.text, letterSpacing: -1.2, lineHeight: 1.05 }}>Esercizi</h1>
      </div>
      <div style={{ padding: "12px 16px 0" }}>
        <style>{`@keyframes gpSlideUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}`}</style>

        {/* ── Esercizi ──────────────────────────────────── */}
        <SearchBar value={search} onChange={setSearch} placeholder="Cerca per nome o muscolo…" style={{ marginBottom: 12, marginTop: 4 }} />

        {/* Usati */}
        {usedGrouped.length > 0 && (
          <>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.subtle, letterSpacing: 0.4, textTransform: "uppercase", marginBottom: 6 }}>I miei esercizi</div>
            {usedGrouped.map(([muscle, exList], gi) => (
              <div key={muscle} style={{ marginBottom: 12, animation: `gpSlideUp 0.45s ${gi * 0.055}s cubic-bezier(0.22,1,0.36,1) both` }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.subtle, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>{muscle}</div>
                <div style={St.card}>
                  {exList.map((e, i, arr) => {
                    const cnt = sessionCount[e.name] || 0;
                    return (
                      <div key={e.name}>
                        <div onClick={() => setSelectedEx(e.name)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 0", cursor: "pointer", WebkitTapHighlightColor: "transparent", transition: "opacity 0.15s" }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 14, color: C.text }}>{e.name}</div>
                            <div style={{ fontSize: 11, color: C.subtle, marginTop: 1 }}>{cnt > 0 ? `${cnt} sessioni` : 'in scheda'}</div>
                          </div>
                          <span style={{ ...St.tag(e.type === "compound" ? C.blue : C.purple), fontSize: 10 }}>{e.type === "compound" ? "Multi" : "Isol."}</span>
                          <svg width="7" height="12" viewBox="0 0 7 12" fill="none"><path d="M1 1l5 5-5 5" stroke="rgba(60,60,67,0.30)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        </div>
                        {i < arr.length - 1 && <div style={{ ...St.divider, margin: "0" }} />}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </>
        )}

        {/* Empty state — schede esistono ma nessun esercizio né sessioni */}
        {usedGrouped.length === 0 && (db.schede || []).some(s => !(s.exercises?.length)) && (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: '32px 24px', gap: 8, marginBottom: 8,
          }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: C.text, textAlign: 'center' }}>
              Le tue schede non hanno esercizi
            </div>
            <div style={{ fontSize: 13, color: C.subtle, textAlign: 'center', lineHeight: 1.5 }}>
              Apri una scheda e aggiungi esercizi per vederli qui.
            </div>
          </div>
        )}

        {/* Libreria completa — nascosta di default */}
        <button onClick={() => setShowLibrary(v => !v)}
          style={{
            width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
            background: "none", border: "none", padding: "10px 0", cursor: "pointer", marginBottom: showLibrary ? 8 : 24
          }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: C.subtle, letterSpacing: 0.4, textTransform: "uppercase" }}>
            Libreria completa
          </span>
          <NavArrowDown width={12} height={12} color={C.subtle} style={{ transition: "transform 0.2s", transform: showLibrary ? "rotate(180deg)" : "rotate(0deg)" }}/>
        </button>
        {showLibrary && (
          <>
            {unusedGrouped.map(([muscle, exList]) => (
              <div key={muscle} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.subtle, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>{muscle}</div>
                <div style={St.card}>
                  {exList.map((e, i, arr) => (
                    <div key={e.name}>
                      <div onClick={() => setSelectedEx(e.name)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 0", cursor: "pointer", WebkitTapHighlightColor: "transparent", transition: "opacity 0.15s" }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 14, color: C.muted }}>{e.name}</div>
                        </div>
                        <span style={{ ...St.tag(e.type === "compound" ? C.blue : C.purple), fontSize: 10, opacity: 0.6 }}>{e.type === "compound" ? "Multi" : "Isol."}</span>
                        <svg width="7" height="12" viewBox="0 0 7 12" fill="none"><path d="M1 1l5 5-5 5" stroke="rgba(60,60,67,0.30)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </div>
                      {i < arr.length - 1 && <div style={{ ...St.divider, margin: "0" }} />}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div style={{ height: 24 }} />
          </>
        )}
      </div>

    </div>
  );
}

